import type { AuthStateUpdate, SetGlobalDataCache } from 'App/AppContextProvider';
import CurrentUser from 'models/users/currentuser';
import Login from 'models/identity/login';
import Tenant from 'models/tenants/tenant';
import User from 'models/users/user';
import UserLanguage from 'models/users/userLanguage';
import logger from 'services/Logging/logService';
import { overflow } from 'utils/string';

//
// The provider-neutral tail of a sign-in: everything after the back-end login answered and the current
// tenant and user are known. Every identity provider runs the same steps with its own token and its own
// source of the user's photo. Throws on failure; the caller's catch signs the user out.
//
// accessTokenApi is the API token of the sign-in, for a step that must call the API before the session is
// in state (none in Phase 1).
//
export const completeLoginProfile = async (
  login: Login,
  currentTenant: Tenant,
  currentUser: User,
  accessTokenApi: string,
  getUserPhoto: () => Promise<Blob | undefined>,
  authStateUpdate: AuthStateUpdate,
  setGlobalDataCache: SetGlobalDataCache,
): Promise<void> => {
  //create new user object
  const usr = new CurrentUser(
    currentUser.id,
    currentTenant,
    currentUser.name,
    currentUser.email,
    login.userLanguageCode || UserLanguage.getBrowserLanguage(),
  );

  usr.login = login;
  usr.identityProviderType = login.identityProviderType;
  usr.providerUserId = currentUser.providerUserId;

  //getting photo
  usr.photo = await getUserPhoto();

  //when user has logged into an organizational unit, show the top level organization in front of its name
  if (currentTenant.parentId && currentTenant.topLevelParentTenantId !== currentTenant.id) {
    logger.debug('Logged into organizational unit', currentTenant);
    const topLevelParent = login.tenants?.find((ou) => ou.id === currentTenant?.topLevelParentTenantId);
    usr.login.isOrgUnit = true;
    if (topLevelParent) {
      usr.tenant.name = overflow(topLevelParent.name, 48) + ' | ' + overflow(currentTenant.name, 48);
    }
  } else {
    usr.tenant.name = overflow(currentTenant.name, 100);
  }

  logger.debug(`Final tenant name: ${usr.tenant.name}`);

  //set the global data cache we got from the login api call and clear the temp cache on the login object
  logger.debug('Init global data cache');
  setGlobalDataCache(usr.login.globalDataCache);
  usr.login.globalDataCache.clear();

  //User is authenticated
  //NOTE: THIS MUST BE DONE BEFORE ANY API CALLS ARE MADE SO THAT THE CORRECT ORG UNIT IS USED
  authStateUpdate(true, usr, false, undefined);
};
